import { mutate } from 'swr';
import { stringify } from 'query-string';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import request, { fetcher, fetchInfinite } from '@dashboard/services/request';
import { apiUrl } from '@dashboard/services/env';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import getSellerCheckoutFromUrl from '@dashboard/services/checkout/getSellerCheckoutFromUrl';
import { pagesKey } from '@dashboard/lib/queryKeys';

export const useDeletePage = (sellerId, token) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: pageId =>
      request(apiUrl + `/sellers/${sellerId}/pages/${pageId}`, token, {
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: pagesKey }),
  });
};

export const useRestorePage = () => {
  const { sellerId, token } = useAuth();

  return useMutation({
    mutationFn: ({ pageId, slug }) =>
      fetcher(`/sellers/${sellerId}/pages/${pageId}/restore`, token, {
        method: 'POST',
        body: JSON.stringify({
          slug,
        }),
      }),
  });
};

export const updatePage = async (sellerId, token, values, pageId) => {
  try {
    const id = pageId || (values && values._id);
    const key = id ? `/sellers/${sellerId}/pages/${id}` : `/sellers/${sellerId}/pages`;

    const response = await request(apiUrl + key, token, {
      body: JSON.stringify(values),
      method: 'POST',
    });

    if (!response) {
      throw new Error();
    }

    if (response?.status === 'error') {
      return { error: response };
    }

    // mutate existing page or add new
    mutate(`/pages/${id ? id : response.data._id}`, response.data);

    return { data: response.data, error: null };
  } catch (error) {
    if (error && error.message) {
      return { error: error.message };
    }

    return { error: 'Unable to save page', data: null };
  }
};

export const attachPageFile = async (sellerId, token, pageId, fileId) =>
  fetcher(`/sellers/${sellerId}/pages/${pageId}/files`, token, {
    method: 'POST',
    body: JSON.stringify({ fileId }),
  });

export const detachPageFile = async (sellerId, token, pageId, fileId) =>
  fetcher(`/sellers/${sellerId}/pages/${pageId}/files/${fileId}`, token, {
    method: 'DELETE',
  });

// Order bumps — all five endpoints answer the same envelope,
// `{ orderBumps: ResolvedOrderBumpEntry[], billingModel }`, so every caller
// applies the response the same way.
export const getPageOrderBumps = async (sellerId, token, pageId) =>
  fetcher(`/sellers/${sellerId}/pages/${pageId}/order-bumps`, token);

export const getPageOrderBumpInfo = async (sellerId, token, pageId, productId) =>
  fetcher(`/sellers/${sellerId}/pages/${pageId}/order-bumps/${productId}/info`, token);

export const attachPageOrderBump = async (sellerId, token, pageId, productId, placement) =>
  fetcher(`/sellers/${sellerId}/pages/${pageId}/order-bumps`, token, {
    method: 'POST',
    body: JSON.stringify({ productId, placement }),
  });

export const updatePageOrderBump = async (sellerId, token, pageId, productId, patch) =>
  fetcher(`/sellers/${sellerId}/pages/${pageId}/order-bumps/${productId}`, token, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

export const detachPageOrderBump = async (sellerId, token, pageId, productId) =>
  fetcher(`/sellers/${sellerId}/pages/${pageId}/order-bumps/${productId}`, token, {
    method: 'DELETE',
  });

export const reorderPageOrderBumps = async (sellerId, token, pageId, productIds) =>
  fetcher(`/sellers/${sellerId}/pages/${pageId}/order-bumps`, token, {
    method: 'PATCH',
    body: JSON.stringify({ productIds }),
  });

export const attachPageImage = async (sellerId, token, pageId, fileId) =>
  fetcher(`/sellers/${sellerId}/pages/${pageId}/images`, token, {
    method: 'POST',
    body: JSON.stringify({ fileId }),
  });

export const detachPageImage = async (sellerId, token, pageId, fileId) =>
  fetcher(`/sellers/${sellerId}/pages/${pageId}/images/${fileId}`, token, {
    method: 'DELETE',
  });

export const getPage = async (sellerId, token, pageId, query = {}) => {
  const qs = stringify(query);

  const key = `/sellers/${sellerId}/pages/${pageId}`;

  const page = await fetcher(`${key}?${qs}`, token);

  mutate(key, page);

  return page;
};

export const saveField = async (sellerId, token, values, fieldId, pageId) => {
  try {
    const key = fieldId
      ? `/sellers/${sellerId}/pages/${pageId}/fields/${fieldId}`
      : `/sellers/${sellerId}/pages/${pageId}/fields`;

    const response = await request(apiUrl + key, token, {
      method: 'POST',
      body: JSON.stringify(values),
    });

    if (!response) {
      throw new Error();
    }

    if (response?.status === 'error') {
      return { error: response };
    }

    return { data: response.data, error: null };
  } catch (error) {
    if (error?.message) {
      return { error: error.message };
    }

    return { error: 'Unable to save field' };
  }
};

export const saveFormLayout = (sellerId, token, pageId, formLayout) =>
  fetcher(`/sellers/${sellerId}/pages/${pageId}/form-layout`, token, {
    method: 'PUT',
    body: JSON.stringify({ formLayout }),
  });

export const duplicateField = (sellerId, token, pageId, fieldId) =>
  fetcher(`/sellers/${sellerId}/pages/${pageId}/fields/${fieldId}/duplicate`, token, {
    method: 'POST',
  });

export const duplicateGroup = (sellerId, token, pageId, groupId) =>
  fetcher(`/sellers/${sellerId}/pages/${pageId}/form-layout/groups/${groupId}/duplicate`, token, {
    method: 'POST',
  });

export const deleteField = async (sellerId, token, fieldId, pageId) =>
  request(`${apiUrl}/sellers/${sellerId}/pages/${pageId}/fields/${fieldId}`, token, {
    method: 'DELETE',
  });

// Only a 204 parses to `true`: error bodies such as the rate limiter's 429 `{ message }` carry no
// `error` key, so checking for one reports a refused send as sent.
const postTestEmail = async (url, token, body) => {
  try {
    const response = await request(url, token, {
      method: 'POST',
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    return response === true;
  } catch {
    return false;
  }
};

export const sendTestEmail = async (sellerId, token, pageId, priceId) => {
  const key = `/sellers/${sellerId}/pages/${pageId}/send-confirmation-email`;
  const sent = await postTestEmail(apiUrl + key, token, priceId ? { priceId } : undefined);

  if (sent) mutate(key, null);

  return sent;
};

export const sendTestReminderEmail = (sellerId, token, pageId, reminderNumber, priceId) =>
  postTestEmail(`${apiUrl}/sellers/${sellerId}/pages/${pageId}/send-test-reminder-email`, token, {
    reminderNumber,
    ...(priceId ? { priceId } : {}),
  });

// react-query
async function getPages(token, sellerId, query) {
  const res = await fetchInfinite(`/sellers/${sellerId}/pages?${stringify(query)}`, token);

  return res;
}

async function getPageSummaries(token, sellerId, query) {
  return fetchInfinite(`/sellers/${sellerId}/page-summaries?${stringify(query)}`, token);
}

export async function getPageCount(token, sellerId) {
  const key = `/sellers/${sellerId}/pages/count`;

  const response = await request(apiUrl + key, token, {
    method: 'GET',
  });

  if (!response) {
    throw new Error('Failed to load page count');
  }

  if (response.error) throw new Error(response.error);

  return response;
}

async function getPageByUrl(token, url) {
  const [seller, checkout] = getSellerCheckoutFromUrl(url);

  if (!seller || !checkout) return null;

  return fetcher(`/sellers/${seller}/pages/${checkout}`, token);
}

export default function usePageQueries() {
  const { token, sellerId } = useAuth();

  return {
    getPageByUrl: url => getPageByUrl(token, url),
    getPage: (pageId, query) => getPage(sellerId, token, pageId, query),
    getPages: (sellerId, query) => getPages(token, sellerId, query),
    getPageSummaries: (sellerId, query) => getPageSummaries(token, sellerId, query),
    getPageCount: sellerId => getPageCount(token, sellerId),
    updatePage: (values, pageId) => updatePage(sellerId, token, values, pageId),
    useDeletePage: () => useDeletePage(sellerId, token),
    saveField: (values, fieldId, pageId) => saveField(sellerId, token, values, fieldId, pageId),
    deleteField: (fieldId, pageId) => deleteField(sellerId, token, fieldId, pageId),
    saveFormLayout: (pageId, formLayout) => saveFormLayout(sellerId, token, pageId, formLayout),
    duplicateField: (pageId, fieldId) => duplicateField(sellerId, token, pageId, fieldId),
    duplicateGroup: (pageId, groupId) => duplicateGroup(sellerId, token, pageId, groupId),
    sendTestEmail: (pageId, priceId) => sendTestEmail(sellerId, token, pageId, priceId),
    sendTestReminderEmail: (pageId, reminderNumber, priceId) =>
      sendTestReminderEmail(sellerId, token, pageId, reminderNumber, priceId),
  };
}
